<template>
  <div
    class="mx-auto max-w-newsletter-container px-small pb-[3.25rem] pt-[2.625rem] lg:px-14 xl:pr-[9.313rem]"
  >
    <div class="flex flex-col gap-small md:flex-row lg:gap-[104px]">
      <div class="w-full lg:w-2/3">
        <div class="flex flex-col gap-5">
          <div
            v-for="(newsletter, index) in NEWSLETTERS"
            :key="index"
            class="flex flex-col items-center gap-5 lg:flex-row"
          >
            <img
              :src="newsletter.img"
              class="h-full max-h-[220px] w-full rounded-xl object-cover lg:max-w-[220px]"
              :alt="newsletter.alt"
            />
            <div>
              <h4 class="mb-3 text-h4">{{ newsletter.title }}</h4>
              <h5 v-if="newsletter.subTitle" class="font-semibold">
                {{ newsletter.subTitle }}
              </h5>
              <p>{{ newsletter.description }}</p>
            </div>
          </div>
        </div>
      </div>
      <div id="hubspot-newsletter-container" class="w-full lg:w-1/3">
        <div class="md:sticky md:top-24 md:-mt-2 lg:top-32">
          <template v-if="authenticated">
            <Form
              v-if="isLoaded"
              v-slot="{ meta, setFieldValue }"
              :initial-values="initialValues"
              @submit="onSubmit"
            >
              <div class="flex flex-col gap-5">
                <h5 v-if="inactiveSubscriptions.length" class="text-h5"
                  >Subscribe to our newsletters</h5
                >
                <TheLoader
                  v-if="
                    inactiveSubscriptions.length === 0 &&
                    activeSubscriptions.length === 0
                  "
                />
                <div
                  v-if="inactiveSubscriptions.length"
                  class="grid w-fit grid-cols-2 gap-x-14 gap-y-2 [&>*]:text-side-nav [&>*]:!mb-0"
                >
                  <Field
                    v-for="(sub, index) in inactiveSubscriptions"
                    v-slot="{ value, handleChange }"
                    :key="sub"
                    :name="sub"
                    type="checkbox"
                  >
                    <FormCheckbox
                      :class="{
                        'is-submitted': isSubmitted,
                        'col-span-1 sm:col-start-2':
                          inactiveSubscriptions.length > 1 &&
                          index === inactiveSubscriptions.length - 1,
                      }"
                      :model-value="value"
                      :label="formatSubscriptionName(sub)"
                      @update:model-value="
                        (val) => {
                          handleChange(val)
                          setFieldValue(sub, val)
                        }
                      "
                    />
                  </Field>
                </div>
                <div
                  v-if="activeSubscriptions.length"
                  class="currently-subscribed flex flex-col gap-2"
                >
                  <h6 class="text-h8">You are currently subscribed to</h6>
                  <div
                    class="grid w-fit grid-cols-2 gap-x-14 gap-y-2 [&>*]:text-side-nav [&>*]:!mb-0"
                  >
                    <FormCheckbox
                      v-for="sub in activeSubscriptions"
                      :key="sub"
                      :label="formatSubscriptionName(sub)"
                      disabled
                      :model-value="true"
                    />
                  </div>
                </div>
                <Button
                  v-if="inactiveSubscriptions.length"
                  :class="{
                    'is-submitted': isSubmitted,
                  }"
                  class="mt-5 w-full"
                  variant="primary"
                  type="submit"
                  :disabled="!meta.dirty"
                >
                  <span v-if="!isSubmitted">Subscribe</span>
                  <span v-else class="flex items-center gap-1"
                    >Subscribed <IconCheckCircle
                  /></span>
                </Button>
              </div>
            </Form>
            <TheLoader v-else class="h-fit" />
          </template>
          <template v-else>
            <template v-if="!displaySuccess">
              <HSForm
                form-type="newsletter"
                form-name="Newsletter"
                :form-id="formId"
                @on-submitted="onFormSubmitted"
              />
            </template>
            <div v-else class="flex flex-col items-center gap-5">
              <IconReadMessage />
              <h6 class="text-h6">
                Thank you for subscribing to our newsletter<span
                  v-if="multipleSubscriptions"
                  >s</span
                >
              </h6>
              <Button
                skip-width-classes
                class="w-full"
                variant="tertiary-outline"
                @click="handleCloseModal"
              >
                Close
              </Button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Form, Field } from 'vee-validate'
import { useGoogleAnalyticsPageView } from '@/composables/google-analytics'
import { NEWSLETTERS } from '@/constants/content'
import HSForm from '@/components/forms/HSForm.vue'
import Button from '@/components/common/BaseButton.vue'
import IconReadMessage from '@/assets/icons/read-message.svg'
import IconCheckCircle from '@/assets/icons/icon-check-circle.svg'
import { useAuthStore } from '@/stores/auth'
import { useUserAccountStore } from '@/stores/userAccount'
import TheLoader from '@/components/TheLoader.vue'
import { logError } from '@/utils/log-error'
import FormCheckbox from '@/components/forms/FormCheckbox.vue'
import type { NuxtApp } from '#app'

const config = useRuntimeConfig()
const formId = config.public.hubspotNewsletterUnauthorizedFormId as string
const authStore = useAuthStore()
const { $seoManager, $rudderstack }: NuxtApp = useNuxtApp()
const userAccountStore = useUserAccountStore()
const { newsletters, loading } = storeToRefs(userAccountStore)

const multipleSubscriptions = ref(false)
const displaySuccess = ref(false)
const isSubmitted = ref(false)

const authenticated = computed(() => authStore.authenticated)
const isLoaded = computed(() => !loading.value && !!newsletters.value)

const seo = $seoManager.generateSeoInfo({
  title: 'Newsletter',
})
useHead(seo)
useGoogleAnalyticsPageView(seo.title)

const initialValues = computed(() => {
  if (!newsletters.value) return {}
  return {
    copperWeekly: newsletters.value.copperWeekly || false,
    policyNewsletter: newsletters.value.policyNewsletter || false,
    priceSpotlight: newsletters.value.priceSpotlight || false,
    benchmarkNewsletter: newsletters.value.benchmarkNewsletter || false,
  }
})

const activeSubscriptions = computed(() => {
  if (!newsletters.value) return []
  return Object.entries(newsletters.value)
    .filter(([_, value]) => value === true)
    .map(([key, _]) => key)
})

const inactiveSubscriptions = computed(() => {
  if (!newsletters.value) return []
  return Object.entries(newsletters.value)
    .filter(([_, value]) => value !== true)
    .map(([key, _]) => key)
})

const handleCloseModal = () => (displaySuccess.value = false)

const formatSubscriptionName = (key: string) => {
  return key
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const onFormSubmitted = ({ values, refreshForm }) => {
  refreshForm()
  const subscriptionKeys = Object.keys(values).filter((key) =>
    [
      'copperWeekly',
      'policyNewsletter',
      'priceSpotlight',
      'benchmarkNewsletter',
    ].includes(key),
  )
  multipleSubscriptions.value =
    subscriptionKeys.filter((key) => values[key] === true).length > 1
  displaySuccess.value = true
}

const onSubmit = async (values, { resetForm }) => {
  try {
    isSubmitted.value = true
    await userAccountStore.updateSubscriptions(values)
    trackNewsletterSubscription(values)
  } catch (error) {
    logError(error)
  } finally {
    isSubmitted.value = false
    resetForm()
  }
}

const NEWSLETTERS_INFO: Record<
  string,
  { newsletter_id: string; newsletter_name: string; newsletter_type: string }
> = {
  copperWeekly: {
    newsletter_id: formId,
    newsletter_type: 'copper_weekly_newsletter',
    newsletter_name: 'Copper Weekly Newsletter',
  },
  policyNewsletter: {
    newsletter_id: formId,
    newsletter_type: 'policy_newsletter',
    newsletter_name: 'Policy Newsletter',
  },
  priceSpotlight: {
    newsletter_id: formId,
    newsletter_type: 'price_spotlight_newsletter',
    newsletter_name: 'Price Spotlight Newsletter',
  },
  benchmarkNewsletter: {
    newsletter_id: formId,
    newsletter_type: 'benchmark_newsletter',
    newsletter_name: 'Benchmark Newsletter',
  },
}

const trackNewsletterSubscription = (values) => {
  const subscriptionKeys = Object.keys(values).filter((key) =>
    [
      'copperWeekly',
      'policyNewsletter',
      'priceSpotlight',
      'benchmarkNewsletter',
    ].includes(key),
  )
  subscriptionKeys.forEach((key) => {
    $rudderstack.trackNewsletterSubscription(NEWSLETTERS_INFO[key])
  })
}

onMounted(() => {
  if (authenticated.value) userAccountStore.fetchUserAccountInfo()
})

watch(authenticated, (value) => {
  if (value) userAccountStore.fetchUserAccountInfo()
})
</script>

<style lang="scss">
#hubspot-newsletter-container {
  .bm-custom-form {
    input[type='submit'].hs-button.primary {
      margin-top: 61px;
    }

    h1 {
      @apply font-semibold;
      margin-bottom: -1rem !important;
    }
  }

  label input[type='checkbox'] + span {
    @apply font-montserrat;
  }

  input[type='checkbox'] {
    @apply text-p-500 border-2;
  }

  .is-submitted input[type='checkbox'] {
    @apply text-good;
  }

  button.is-submitted {
    @apply bg-semantic-good-light border-semantic-good-light;
  }

  .currently-subscribed {
    span {
      @apply text-s-600;
    }
    input[type='checkbox'] {
      @apply bg-s-400;
      border: 1px;
    }
  }

  .btn {
    height: 42px;
    @apply text-sm;
  }
}
</style>
